import { XMarkIcon } from "@heroicons/react/24/outline";
import { Button, ButtonGroup, Checkbox, Table } from "north.js";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import { PageBar, Spinner } from "src/shared/components";
import { errorToast, successToast } from "src/shared/components/Toast";

import { useUpdateVolunteerShiftsMutation } from "../graphql/updateVolunteerShifts.generated";
import { TShiftSchedule } from "../types";
import { shiftMatchesSearch } from "../utils";

interface ListViewScheduleProps {
  fetchingShifts: boolean;
  adminShifts: TShiftSchedule[];
  search: string;
  page: number;
  onPageChange: (page: number) => void;
  setSelectedShift: React.Dispatch<React.SetStateAction<number | null>>;
  setViewModal: React.Dispatch<React.SetStateAction<boolean>>;
  onRegistrationChange?: () => void;
}

type DragSelectState = {
  mode: "add" | "remove";
  /** working set for the active drag, avoids skipped mouseenter + stale state */
  next: Set<number>;
  lastId: number | null;
};

const SHIFT_SELECT_ATTR = "data-shift-select-id";

const shiftIdFromPoint = (clientX: number, clientY: number): number | null => {
  const el = document.elementFromPoint(clientX, clientY);
  const hit = el?.closest(`[${SHIFT_SELECT_ATTR}]`) as HTMLElement | null;
  if (!hit) return null;
  const id = Number(hit.getAttribute(SHIFT_SELECT_ATTR));
  return Number.isFinite(id) ? id : null;
};

const AdminShiftListView: React.FC<ListViewScheduleProps> = ({
  fetchingShifts,
  adminShifts,
  search,
  page,
  onPageChange,
  setSelectedShift,
  setViewModal,
  onRegistrationChange,
}) => {
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [selectedShiftIds, setSelectedShiftIds] = useState<number[]>([]);
  const [isDragSelecting, setIsDragSelecting] = useState(false);
  const dragSelectRef = useRef<DragSelectState | null>(null);
  const [updateVolunteerShifts, { loading: updating }] =
    useUpdateVolunteerShiftsMutation();

  // category filters are already applied upstream in adminShifts; search
  // narrows further here, so "select all" always means the visible set
  const filteredShifts = useMemo(
    () => adminShifts.filter((shift) => shiftMatchesSearch(shift, search)),
    [adminShifts, search]
  );

  const filteredIds = useMemo(
    () => filteredShifts.map((shift) => shift.shift_id),
    [filteredShifts]
  );
  const filteredIdsRef = useRef(filteredIds);
  filteredIdsRef.current = filteredIds;

  const allFilteredSelected =
    filteredIds.length > 0 &&
    filteredIds.every((id) => selectedShiftIds.includes(id));

  const handleShiftClick = (shift: TShiftSchedule) => {
    setSelectedShift(shift.shift_id);
    setViewModal(true);
  };

  /** Paint every id from the last hit through this one so fast moves don't skip. */
  const applyDragSelectToId = (shiftId: number) => {
    const drag = dragSelectRef.current;
    if (!drag || drag.lastId === shiftId) return;

    const ids = filteredIdsRef.current;
    const fromIdx = drag.lastId == null ? -1 : ids.indexOf(drag.lastId);
    const toIdx = ids.indexOf(shiftId);
    const indices =
      fromIdx === -1 || toIdx === -1
        ? [toIdx].filter((i) => i >= 0)
        : Array.from(
            { length: Math.abs(toIdx - fromIdx) + 1 },
            (_, i) => Math.min(fromIdx, toIdx) + i
          );

    for (const idx of indices) {
      const id = ids[idx];
      if (drag.mode === "add") drag.next.add(id);
      else drag.next.delete(id);
    }
    drag.lastId = shiftId;
    setSelectedShiftIds(Array.from(drag.next));
  };

  const endDragSelect = () => {
    dragSelectRef.current = null;
    setIsDragSelecting(false);
    document.body.style.removeProperty("user-select");
  };

  const handleCheckboxPointerDown = (
    e: React.PointerEvent,
    shiftId: number
  ) => {
    if (e.button !== 0) return;
    e.preventDefault();
    // don't capture — we need elementFromPoint to see other checkboxes while moving

    const mode: "add" | "remove" = selectedShiftIds.includes(shiftId)
      ? "remove"
      : "add";
    const next = new Set(selectedShiftIds);
    if (mode === "add") next.add(shiftId);
    else next.delete(shiftId);

    dragSelectRef.current = { mode, next, lastId: shiftId };
    document.body.style.userSelect = "none";
    setIsDragSelecting(true);
    setSelectedShiftIds(Array.from(next));
  };

  useEffect(() => {
    if (!isDragSelecting) return;

    const onMove = (e: PointerEvent) => {
      const id = shiftIdFromPoint(e.clientX, e.clientY);
      if (id != null) applyDragSelectToId(id);
    };
    const onUp = () => endDragSelect();

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [isDragSelecting]);

  // drop selections that are no longer in the filtered list (search/filter change)
  useEffect(() => {
    const visibleIds = new Set(filteredShifts.map((s) => s.shift_id));
    setSelectedShiftIds((prev) => prev.filter((id) => visibleIds.has(id)));
  }, [filteredShifts]);

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      setSelectedShiftIds([]);
      return;
    }
    setSelectedShiftIds(filteredIds);
  };

  const setRegistrationOpen = async (registration_open: boolean) => {
    if (selectedShiftIds.length === 0) return;
    try {
      await updateVolunteerShifts({
        variables: {
          where: { shift_id: { in: selectedShiftIds } },
          data: { registration_open },
        },
      });
      successToast(
        registration_open
          ? `Opened registration for ${selectedShiftIds.length} shift${
              selectedShiftIds.length === 1 ? "" : "s"
            }`
          : `Closed registration for ${selectedShiftIds.length} shift${
              selectedShiftIds.length === 1 ? "" : "s"
            }`
      );
      setSelectedShiftIds([]);
      onRegistrationChange?.();
    } catch {
      errorToast("Couldn't update registration. Try again.");
    }
  };

  const tableData = filteredShifts.map((shift: TShiftSchedule) => {
    const selected = selectedShiftIds.includes(shift.shift_id);
    return {
      select: (
        <CheckboxHitArea
          data-shift-select-id={shift.shift_id}
          $dragging={isDragSelecting}
          onPointerDown={(e) => handleCheckboxPointerDown(e, shift.shift_id)}
        >
          <Checkbox
            checked={selected}
            // selection is handled by pointer down / drag on the hit area
            onChange={() => undefined}
          />
        </CheckboxHitArea>
      ),
      shift: shift?.shift,
      date: shift?.date,
      time_start: (
        <div style={{ textAlign: "right" }}>
          {shift?.time_start
            .toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "numeric",
              hour12: true,
              timeZone: "America/New_York",
            })
            .replace(":00", "")}
        </div>
      ),
      time_end: (
        <div style={{ textAlign: "right" }}>
          {shift?.time_end
            .toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "numeric",
              hour12: true,
              timeZone: "America/New_York",
            })
            .replace(":00", "")}
        </div>
      ),
      status: (
        <StatusPill $open={shift.registration_open}>
          {shift.registration_open ? "Open" : "Closed"}
        </StatusPill>
      ),
      spots_left: (
        <div style={{ textAlign: "center" }}>{shift?.spots_left}</div>
      ),
      roles: <RolesContainer>{shift?.role}</RolesContainer>,
      view: (
        <ViewButton
          onClick={() => {
            handleShiftClick(shift);
          }}
        >
          View
        </ViewButton>
      ),
    };
  });

  const leftAlignment: "left" | "center" | "right" = "left";
  const centerAlignment: "left" | "center" | "right" = "center";
  const rightAlignment: "left" | "center" | "right" = "right";

  const tableColumns = [
    {
      id: "select",
      name: "",
      hideLabel: true,
      align: leftAlignment,
    },
    {
      id: "shift",
      name: "SHIFT",
      align: leftAlignment,
    },
    {
      id: "date",
      name: "DATE",
      align: leftAlignment,
    },
    {
      id: "time_start",
      name: "TIME START",
      align: rightAlignment,
    },
    {
      id: "time_end",
      name: "TIME END",
      align: rightAlignment,
    },
    {
      id: "status",
      name: "REGISTRATION",
      align: leftAlignment,
    },
    {
      id: "spots_left",
      name: "SPOTS LEFT",
      align: centerAlignment,
    },
    {
      id: "roles",
      name: "ROLES",
      align: leftAlignment,
    },
    {
      id: "view",
      name: "View",
      hideLabel: true,
      align: leftAlignment,
    },
  ];

  const selectAllLabel = allFilteredSelected
    ? `Deselect all ${filteredIds.length} matching`
    : `Select all ${filteredIds.length} matching`;

  return (
    <ListViewRoot>
      {filteredIds.length > 0 && (
        <SelectAllBar>
          <Checkbox
            checked={allFilteredSelected}
            onChange={toggleSelectAllFiltered}
            label={selectAllLabel}
          />
        </SelectAllBar>
      )}
      {fetchingShifts ? (
        <Spinner text="Loading shifts..." />
      ) : (
        <>
          <Table
            columns={tableColumns}
            data={tableData}
            horizontalPadding={16}
            verticalPadding={10}
            showFooter
            page={page}
            itemsPerPage={itemsPerPage}
          />
          <PageBar
            page={page}
            itemsPerPage={itemsPerPage}
            totalItems={tableData.length}
            onPageChange={onPageChange}
            onItemsPerPageChange={setItemsPerPage}
          />
        </>
      )}
      {selectedShiftIds.length > 0 && (
        <ActionDrawer>
          <ButtonGroup>
            <Button
              color="primary"
              disabled={updating}
              onClick={() => setRegistrationOpen(true)}
            >
              Open all
            </Button>
            <Button
              color="secondary-danger"
              disabled={updating}
              onClick={() => setRegistrationOpen(false)}
            >
              Close all
            </Button>
          </ButtonGroup>
          <ButtonGroup>
            <Button>{selectedShiftIds.length} selected</Button>
            <Button
              leadingIcon={<XMarkIcon className="w-4 h-4" />}
              color="secondary"
              disabled={updating}
              onClick={() => setSelectedShiftIds([])}
              aria-label="Clear selection"
            />
          </ButtonGroup>
        </ActionDrawer>
      )}
    </ListViewRoot>
  );
};

const CheckboxHitArea = styled.div<{ $dragging: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  /* fill the table cell so fast vertical drags don't fall into gaps */
  min-width: 44px;
  min-height: 44px;
  margin: -10px -16px;
  padding: 10px 16px;
  cursor: pointer;
  user-select: none;
  touch-action: none;

  ${({ $dragging }) =>
    $dragging &&
    `
    cursor: pointer;
    /* let elementFromPoint hit the hit-area, not the nested input */
    & * {
      pointer-events: none;
    }
  `}
`;

const SelectAllBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const RolesContainer = styled.div`
  border-radius: 27.568px;
  padding: 4px 9.189px;
  background: #e5e7eb;
  text-align: center;
  font-weight: 700;
  font-size: 11.748px;
  color: #6b7280;
`;

const StatusPill = styled.div<{ $open: boolean }>`
  border-radius: 27.568px;
  padding: 4px 9.189px;
  text-align: center;
  font-weight: 700;
  font-size: 11.748px;
  background: ${({ $open }) => ($open ? "#d1fae5" : "#e5e7eb")};
  color: ${({ $open }) => ($open ? "#047857" : "#6b7280")};
`;

const ViewButton = styled.div`
  border-radius: 4px;
  border: 1px solid #d1d5db;
  background: #fff;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
  color: #1f2937;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
  padding: 2px 8px;
  width: fit-content;

  &:hover {
    cursor: pointer;
  }
`;

const ListViewRoot = styled.div`
  position: relative;
`;

const ActionDrawer = styled.div`
  position: sticky;
  bottom: 42px;
  z-index: 20;
  display: flex;
  gap: 8px;
  width: fit-content;
  margin: 42px auto 0;
  padding: 8px;
  background: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
`;

export default AdminShiftListView;
